import { ApiOutlined } from '@ant-design/icons';
import { Alert, Col, Form, Row, Select, Switch, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import IntegrationCard from 'components/IntegrationCard/IntegrationCard';

import { KitCompany } from 'models/KitCompany';

import { FunctionsHelper } from 'utils/FunctionsHelper';

const I18N_BASE = 'scenes.settings.companies.components.kit_integration_fields';

export const KIT_COMPANIES_FIELD = 'kitCompanyIds';

interface KitIntegrationFieldsProps {
  enabled: boolean;
  onEnabledChange: (checked: boolean) => void;
  kitCompanies: KitCompany[];
  kitCompaniesLoading?: boolean;
  disabled?: boolean;
  /**
   * Al crear, el catálogo de KIT depende del país: hasta que no se elige uno no
   * hay nada que ofrecer.
   */
  selectDisabled?: boolean;
  /** Ids elegidos en el formulario, para el resumen del header. */
  selectedCompanyIds?: number[];
}

function KitIntegrationFields({
  enabled,
  onEnabledChange,
  kitCompanies,
  kitCompaniesLoading = false,
  disabled = false,
  selectDisabled = false,
  selectedCompanyIds = [],
}: KitIntegrationFieldsProps) {
  const { t } = useTranslation();

  const selectedCount = selectedCompanyIds.length;

  const summary = !enabled ? (
    <Tag>{t(`${I18N_BASE}.summary.disabled`)}</Tag>
  ) : selectedCount > 0 ? (
    <Tag color="blue">
      {t(`${I18N_BASE}.summary.companies`, { count: selectedCount })}
    </Tag>
  ) : (
    <Tag color="red">{t(`${I18N_BASE}.summary.missing_companies`)}</Tag>
  );

  return (
    <IntegrationCard
      title={t(`${I18N_BASE}.title`)}
      icon={<ApiOutlined />}
      summary={summary}
    >
      <Row gutter={24}>
        <Col xs={24}>
          <Form.Item style={{ marginBottom: enabled ? 16 : 0 }}>
            <Switch
              checked={enabled}
              onChange={onEnabledChange}
              disabled={disabled}
            />
            <span style={{ marginLeft: 10 }}>
              {t(`${I18N_BASE}.labels.enable`)}
            </span>
          </Form.Item>
        </Col>
      </Row>

      {/*
        El catálogo de KIT se pide por país. Sin país el selector queda
        bloqueado, así que hay que decir por qué: el campo está en otra card y
        la dependencia no se ve.
      */}
      {enabled && selectDisabled && (
        <Alert
          type="info"
          showIcon
          message={t(`${I18N_BASE}.alerts.country_required`)}
        />
      )}

      {enabled && !selectDisabled && (
        <Row gutter={24}>
          <Col xs={24}>
            <Form.Item
              name={KIT_COMPANIES_FIELD}
              label={t(`${I18N_BASE}.labels.companies`)}
              required
              style={{ marginBottom: 0 }}
              rules={[
                {
                  validator: (_, value) =>
                    value && value.length > 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            t(`${I18N_BASE}.validations.companies.required`)
                          )
                        ),
                },
              ]}
            >
              <Select
                mode="multiple"
                options={kitCompanies.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                showSearch
                allowClear
                disabled={disabled || kitCompaniesLoading}
                loading={kitCompaniesLoading}
                filterOption={(search: string, option?: any) =>
                  FunctionsHelper.normalizeText(option?.label ?? '').includes(
                    FunctionsHelper.normalizeText(search)
                  )
                }
                placeholder={t(`${I18N_BASE}.placeholders.companies`)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </IntegrationCard>
  );
}

export default KitIntegrationFields;
